import { getAppEnvironment, getRuntimeConfigValue } from '@/app/runtime';
import { normalizeFeatureFlagValue } from '@/utils/featureFlags';

/**
 * @fileoverview Global feature flags configuration.
 *
 * This file is the single source of truth for feature availability across the
 * application.  Features are enabled or disabled by editing this file and
 * deploying a new release — there is no runtime toggle or localStorage
 * override.
 *
 * Individual users can still receive early access to a feature when the
 * backend includes the corresponding feature-flag key in the user's
 * `active_entitlements` array.  The composable `useFeatureFlags` combines
 * both sources (this config + user entitlements) to decide visibility.
 *
 * @example
 * // Enable the Dashboard Builder for everyone:
 * DASHBOARD_BUILDER: true,
 *
 * // Release the Graph Builder to internal users only:
 * GRAPH_BUILDER: 'internal',
 *
 * // Keep the Graph Builder behind an entitlement for now:
 * GRAPH_BUILDER: false,
 */

/**
 * @typedef {Object} FeatureFlagsConfig
 * @property {boolean | 'internal'} DASHBOARD_BUILDER   - Show/hide the Dashboard Builder for all users, or internal users only.
 * @property {boolean | 'internal'} GRAPH_BUILDER       - Show/hide the Graph Builder for all users, or internal users only.
 * @property {boolean | 'internal'} KPI_VISUALIZATIONS  - Show/hide KPI visualization creation and discovery flows.
 * @property {boolean | 'internal'} INDICATOR_SETTINGS  - Show/hide chart indicator settings for all users, or internal users only.
 * @property {boolean | 'internal'} API_DOCS_CATALOG    - Show/hide API docs catalog storefront, or internal users only.
 * @property {boolean | 'internal'} HEALTH_STATUS_INDICATORS - Show/hide dataset, series, and visualization health indicators, or internal users only.
 * @property {boolean | 'internal'} POWER_MARKET_SIMULATOR - Show/hide power market simulator tool, or internal users only.
 * @property {boolean | 'internal'} ENVIRONMENT_ASSETS - Show/hide My environment Assets surfaces while the demo is unreleased.
 * @property {boolean | 'internal'} SHADOW_TRADING - Show/hide shadow trading tool. Hidden in production; internal-only elsewhere.
 * @property {boolean | 'internal'} FORWARD_BENCHMARK - Show/hide forward benchmark tool. Hidden in production; on elsewhere.
 * @property {boolean | 'internal'} HEDGING_BENCHMARK - Show/hide the internal historical hedging tool.
 * @property {boolean | 'internal'} HEDGING_HISTORICAL_ROUTE - Open new hedging benchmarks in the four-step historical comparison.
 * @property {boolean | 'internal'} CAPACITY_FEE_FORECASTER - Show/hide capacity fee forecaster tool. Hidden by default while unreleased.
 * @property {boolean | 'internal'} SPIKE_PROBABILITY - Show/hide spike probability forecast tool. Internal-only while in development.
 * @property {boolean | 'internal'} CHANGELOG_DIALOG - Show/hide the "what's new" dialog after a notable release.
 * @property {boolean | 'internal'} ALERT_REGISTRY_ALLOWLIST - Treat the alert target registry as an allowlist in all alert pickers.
 * @property {boolean | 'internal'} BIDDING_STRATEGY_SITE_LOAD - Show/hide the site-load block on the bidding-strategy wizard's Connection & site step.
 * @property {boolean | 'internal'} GRID_TARIFF_TIME_OF_USE - Kill switch for the time-of-use grid tariff (PI-531). Live from merge.
 * @property {boolean | 'internal'} SHADOW_GRID_CONNECTION - Shadow trading: connection caps, GTV, congestion area and energy tax (PI-654).
 * @property {boolean | 'internal'} SHADOW_SITE - Shadow trading: site load and generation behind the battery's meter (PI-654).
 * @property {boolean | 'internal'} SHADOW_DEGRADATION - Shadow trading: the fade model for battery wear (PI-654).
 */

const parseEnvFlags = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed.reduce((acc, key) => {
        if (typeof key === 'string' && key.trim()) acc[key.trim()] = true;
        return acc;
      }, {});
    }
    if (parsed && typeof parsed === 'object') {
      return Object.entries(parsed).reduce((acc, [key, value]) => {
        const normalizedKey = key?.trim?.();
        if (normalizedKey) {
          acc[normalizedKey] = normalizeFeatureFlagValue(value);
        }
        return acc;
      }, {});
    }
  } catch (error) {
    // fall back to comma-separated list
  }

  return trimmed.split(',').reduce((acc, segment) => {
    const [rawKey, ...rawValueParts] = segment.split('=');
    const normalizedKey = rawKey?.trim?.();
    if (!normalizedKey) return acc;

    if (rawValueParts.length === 0) {
      acc[normalizedKey] = true;
      return acc;
    }

    const rawValue = rawValueParts.join('=').trim();
    acc[normalizedKey] = rawValue ? normalizeFeatureFlagValue(rawValue) : true;
    return acc;
  }, {});
};

const environment = (process.env.VUE_APP_ENVIRONMENT || 'production').toLowerCase();
const envOverrides = {
  local: parseEnvFlags(process.env.VUE_APP_FEATURE_FLAGS_LOCAL),
  acceptance: parseEnvFlags(process.env.VUE_APP_FEATURE_FLAGS_ACCEPTANCE),
  production: parseEnvFlags(process.env.VUE_APP_FEATURE_FLAGS_PRODUCTION),
}[environment];

/** @type {FeatureFlagsConfig} */
const featureFlags = {
  /**
   * Dashboard Builder — allows users to create and manage custom dashboards
   * with drag-and-drop panel editing.
   */
  DASHBOARD_BUILDER: true, // enabled for internal users only

  /**
   * Graph Builder — allows users to create custom visualizations by
   * combining series from one or more datasets.
   */
  GRAPH_BUILDER: false, // hide in production

  /**
   * KPI Visualizations — allows KPI creation and KPI marketplace discovery.
   */
  KPI_VISUALIZATIONS: true,

  /**
   * Indicator Settings — allows users to configure technical indicators on charts.
   */
  INDICATOR_SETTINGS: true,

  /**
   * API Docs Catalog — the API store (PI-635): the catalogue, cart and checkout,
   * the /api customer pages and the purchase box on dataset pages.
   * On in acceptance/local/development. In production it follows the runtime
   * `VUE_APP_API_STORE` (deploy overlay, read at container start), so opening
   * or closing the store is a config change, not a rebuild. Unset = off there;
   * super-users still see it to test.
   */
  API_DOCS_CATALOG:
    ['acceptance', 'local', 'development'].includes(
      String(getAppEnvironment() || '').toLowerCase()
    ) ||
    // ponytail: anything but true/'internal' (a typo included) keeps it closed.
    [true, 'internal'].find(
      (v) => v === normalizeFeatureFlagValue(getRuntimeConfigValue('VUE_APP_API_STORE'))
    ) ||
    false,

  /**
   * Health Status Indicators — dataset, visualization, and series health status UI.
   */
  HEALTH_STATUS_INDICATORS: true,

  /**
   * Power Market Simulator — internal simulator tool.
   */
  POWER_MARKET_SIMULATOR: true,

  /**
   * Environment Assets — Withthegrid demo telemetry in My environment.
   * Hidden from release navigation until the product surface is ready.
   */
  ENVIRONMENT_ASSETS: false,

  /**
   * Shadow Trading — internal shadow trading tool.
   * Hidden in production (entitlement-only: super-users + explicit
   * `shadow-trading` entitlement). Enabled for everyone on
   * acceptance/local so reviewers can test.
   */
  SHADOW_TRADING: true,

  /**
   * Forward Benchmark — bidding-strategy / P&L forecast tool.
   * Hidden in production (entitlement-only: super-users + explicit
   * `forward-benchmark` entitlement). Enabled for everyone on
   * acceptance/local.
   */
  FORWARD_BENCHMARK: true,

  /**
   * Hedging Benchmark — historical supplier-style percentage hedge analysis.
   * Released and SOLD (Basic + Combi; Flex excluded). `true` like the other
   * released tools: the nav entry is visible to everyone and enforcement lives
   * downstream, where every route carries
   * RequireToolKeyEntitlementTokenData("hedging-benchmark") ->
   * tool.hedging-benchmark.use and AUTHENTICATED_ONLY_TOOL_ROUTE_KEYS is empty.
   *
   * Neither other value works here. 'internal' short-circuits before the
   * entitlement check, so it hid the tool from every paying customer while
   * showing it to any @profiteia.io/@bakeable.nl/@giga-storage.com address
   * regardless of plan. `false` falls back to
   * activeEntitlements.includes('hedging-benchmark') — the bare flag key, which
   * exists in no registry (only tool.hedging-benchmark.use does), so it would
   * hide the tool from everyone except super-users.
   */
  HEDGING_BENCHMARK: 'internal',

  /**
   * Hedging Benchmark historical route — new comparisons open in the four-step
   * Profiel → Product → Strategie → Resultaat flow; forward planning stays under
   * Geavanceerd. Internal first; switching it off restores the previous route
   * while stored runs keep opening where they were made.
   */
  HEDGING_HISTORICAL_ROUTE: 'internal',

  /**
   * Capacity Fee Forecaster — FCR/aFRR capacity fee forecast tool.
   * Released — available to all users (still entitlement-gated downstream).
   */
  CAPACITY_FEE_FORECASTER: true,

  /**
   * Spike Probability — ISP-level spike probability forecast tool.
   * Released — available to all users (still entitlement-gated downstream).
   */
  SPIKE_PROBABILITY: true,

  /**
   * Changelog dialog — the one-time "what's new" dialog after a release the
   * operator marked notable.
   *
   * 'internal' for the soft launch. NB: flag resolution is email-domain based,
   * not environment based, so this DOES show to @profiteia.io users in
   * production — which is the point: we want to see it against a real release
   * before it interrupts customers. Flip to `true` once one release has been
   * through the whole flow cleanly.
   *
   * The What's new PAGE is not gated — only the interruption is.
   */
  CHANGELOG_DIALOG: true,

  /**
   * Reports — auto-generated premium battery-benchmark reports (PPTX + portal).
   * MUST NOT ship to production. Disabled globally (entitlement-only): only
   * super-users or users granted the `report-builder` entitlement can reach it,
   * in any environment. NB: 'internal' is NOT sufficient to hide it in prod —
   * flag resolution is email-domain based, not environment based, so 'internal'
   * would expose it to @profiteia.io/@bakeable.nl/@giga-storage.com users on
   * prod. Grant `report-builder` for internal/acceptance testing; flip to
   * 'internal'/true only when cleared for release.
   */
  REPORTS: false,

  /**
   * Water Risk — European hydrological stress + modelled MW-at-risk monitor.
   * Released — available to all users (still entitlement-gated downstream).
   */
  WATER_RISK: true,

  /** Dutch distribution interruption monitor — internal operational use only. */
  DISTRIBUTION_INTERRUPTIONS: 'internal',

  /**
   * Alert target registry as allowlist in all pickers. `'internal'` =
   * super-users + every domain in `INTERNAL_FEATURE_EMAIL_DOMAINS`
   * (`src/utils/featureFlags.js`: profiteia.io, bakeable.nl, giga-storage.com)
   * only (note: with `'internal'` `isFeatureEnabled` never consults
   * entitlements — an `alert-registry-allowlist` entitlement only matters once
   * this is `false`). Flip to `true` together with backend
   * `ALERT_TARGET_REGISTRY_MODE=allowlist` (design §7).
   */
  ALERT_REGISTRY_ALLOWLIST: 'internal',
  // Site load (a load sharing the battery's grid connection) is not validated
  // yet. On for acceptance so it can be tested (runtime environment, so this
  // file can merge to master safely); off everywhere else — `false`, not
  // 'internal', because 'internal' still exposes it to internal domains in
  // production. The rest of step 3 (connection capacity, congestion contracts)
  // is unaffected.
  BIDDING_STRATEGY_SITE_LOAD: getAppEnvironment() === 'acceptance',

  /**
   * Time-of-use grid tariff (PI-531) — the `kWmaxgewogen` weighted-peak
   * transport charge, in the v4 bidding-strategy benchmark and in shadow
   * trading.
   *
   * `true` on purpose: this is a KILL SWITCH, not a staging gate. The feature
   * is live for everyone the moment it merges and defaults ON for a new v4
   * benchmark (decision L6) — the headline EUR/MW/yr is only honest with
   * transport cost in it. Flip to `false` to pull the block and drop the
   * payload key without a deploy; the engine no-ops without it.
   */
  GRID_TARIFF_TIME_OF_USE: true,

  // PI-654 in shadow trading. Mirrors the backend's SHADOW_*_ENABLED gates,
  // which refuse these payloads in production until the release: off there,
  // on everywhere else.
  SHADOW_GRID_CONNECTION: getAppEnvironment() !== 'production',
  SHADOW_SITE: getAppEnvironment() !== 'production',
  SHADOW_DEGRADATION: getAppEnvironment() !== 'production',
};

export default {
  ...featureFlags,
  ...(envOverrides || {}),
};
